import logger from '~/services/logger';
import type { FeedEntry } from '~/services/layout/index.ts';
import { isRecord } from '~/services/layout/layout.ts';
import {
  BLOCKED_POWER_CELL_RIGHT_CLICK_ALIASES,
  getActionAlias,
} from './contextual-action-policy.ts';

/** Identifier standing for "whatever this entry declares", not a plugin. */
export const ENTRY_ACTIONS_IDENTIFIER: string = 'entry_actions';

/** An `extensions.entry_action` item, as read by the popup-menu data hook. */
export type PopupMenuEntryAction = Record<string, unknown>;

/** The entry-action-shaped favourites item injected into the menu. */
export type FavoritesMenuItem = {
  button: { alias: string };
  actions: Array<{ type: string }>;
  dismiss_on_action: boolean;
  label: string;
  icon: string;
};

export type ResolvedPopupMenuItem =
  | { kind: 'entry_action'; entryAction: PopupMenuEntryAction }
  | { kind: 'favorites' };

export type ResolvedPopupMenuItems = {
  items: ResolvedPopupMenuItem[];
  hasRenderableItems: boolean;
};

export type ResolvePopupMenuActionItemsArgs = {
  identifiers: string | string[] | null | undefined;
  entry: FeedEntry;
  favoritesAvailable: boolean;
};

export type BuildFavoritesMenuItemArgs = {
  defaultLabel?: string;
  activeLabel?: string;
  isFavorite: boolean;
  isLoggedIn: boolean;
};

export type BuildResolvedContextualEntryArgs = {
  entry: FeedEntry;
  items: ResolvedPopupMenuItem[];
  favoritesItem: FavoritesMenuItem;
};

export type GetResolvedPopupMenuStateArgs = {
  popupMenuActionsConfig: string | string[];
  entry: FeedEntry;
  favoritesAvailable: boolean;
  favoritesItem: FavoritesMenuItem;
};

export type PopupMenuResolvedState = {
  contextualEntry: FeedEntry;
  hasRenderableActions: boolean;
};

type PopupMenuActionProviderContext = {
  entryActionItems: PopupMenuEntryAction[];
  favoritesAvailable: boolean;
};

type PopupMenuActionProvider = (
  context: PopupMenuActionProviderContext
) => ResolvedPopupMenuItem[];

const reportedWarningKeys = new Set<string>();

/**
 * Logs a warning once per key, so re-resolving on every render does not
 * flood the logs.
 */
function warnOnce({ key, message }: { key: string; message: string }): void {
  if (reportedWarningKeys.has(key)) {
    return;
  }

  reportedWarningKeys.add(key);

  logger.warn(message);
}

/**
 * Splits a configured actions list into identifiers.
 * Accepts a comma separated string or an array; trims and drops empty tokens.
 */
export function parseActionIdentifiers(
  config?: string | string[] | null
): string[] {
  if (config === null || config === undefined) {
    return [];
  }

  return (Array.isArray(config) ? config : config.split(','))
    .map((identifier: string) => identifier.trim())
    .filter(Boolean);
}

/**
 * Returns the entry's own actions, dropping (with a one-time warning per
 * entry and position) items that are not objects.
 */
function getEntryActionItems(entry: FeedEntry): PopupMenuEntryAction[] {
  const entryAction: unknown = entry?.extensions?.entry_action;

  if (!Array.isArray(entryAction)) {
    return [];
  }

  return entryAction.filter(
    (item: unknown, index: number): item is PopupMenuEntryAction => {
      if (isRecord(item)) {
        return true;
      }

      warnOnce({
        key: `malformed:${entry?.id}:${index}`,
        message: `resolvePopupMenuActionItems: dropping malformed entry action at index ${index} of entry "${entry?.id}" - entry actions must be objects`,
      });

      return false;
    }
  );
}

/**
 * Whether the entry action is a queue-open alias that is not actionable from
 * a cell popup.
 */
function isBlockedEntryAction(entryAction: PopupMenuEntryAction): boolean {
  const alias: string | undefined = getActionAlias(entryAction);

  if (!alias) {
    return false;
  }

  return BLOCKED_POWER_CELL_RIGHT_CLICK_ALIASES.has(alias);
}

/**
 * Expands `entry_actions` into the entry's own actions in a single pass,
 * skipping queue-open aliases that are not actionable from a cell popup.
 */
const resolveEntryActions: PopupMenuActionProvider = ({
  entryActionItems,
}: PopupMenuActionProviderContext): ResolvedPopupMenuItem[] =>
  entryActionItems.reduce(
    (
      items: ResolvedPopupMenuItem[],
      entryAction: PopupMenuEntryAction
    ): ResolvedPopupMenuItem[] => {
      if (!isBlockedEntryAction(entryAction)) {
        items.push({ kind: 'entry_action', entryAction });
      }

      return items;
    },
    []
  );

/**
 * Resolves a favourites identifier to the favourites item when favourites
 * are available for the entry.
 */
const resolveFavorites: PopupMenuActionProvider = ({
  favoritesAvailable,
}: PopupMenuActionProviderContext): ResolvedPopupMenuItem[] =>
  favoritesAvailable ? [{ kind: 'favorites' }] : [];

/**
 * Configured identifier -> menu items. A Map rather than an object literal so
 * an identifier such as "constructor" cannot resolve to an inherited member.
 */
const POPUP_MENU_ACTION_PROVIDERS: ReadonlyMap<
  string,
  PopupMenuActionProvider
> = new Map<string, PopupMenuActionProvider>([
  [ENTRY_ACTIONS_IDENTIFIER, resolveEntryActions],
  ['local_storage_favourites_action', resolveFavorites],
  ['local_storage_favourites_action_web', resolveFavorites],
]);

/**
 * Resolves a configured popup-menu actions list into menu items through
 * `POPUP_MENU_ACTION_PROVIDERS`. Unknown identifiers are dropped with a
 * one-time warning. Order is preserved and repeated identifiers are
 * intentionally not deduped.
 */
export function resolvePopupMenuActionItems({
  identifiers,
  entry,
  favoritesAvailable,
}: ResolvePopupMenuActionItemsArgs): ResolvedPopupMenuItems {
  const context: PopupMenuActionProviderContext = {
    entryActionItems: getEntryActionItems(entry),
    favoritesAvailable,
  };

  const items: ResolvedPopupMenuItem[] = parseActionIdentifiers(
    identifiers
  ).flatMap((identifier: string): ResolvedPopupMenuItem[] => {
    const normalizedIdentifier: string = identifier.toLowerCase();
    const provider: PopupMenuActionProvider | undefined =
      POPUP_MENU_ACTION_PROVIDERS.get(normalizedIdentifier);

    if (!provider) {
      warnOnce({
        key: `unknown:${normalizedIdentifier}`,
        message: `resolvePopupMenuActionItems: configured action "${normalizedIdentifier}" has no web implementation - its item will not be shown`,
      });

      return [];
    }

    return provider(context);
  });

  return { items, hasRenderableItems: items.length > 0 };
}

/**
 * Builds the entry-action-shaped favourites item injected into the menu.
 * Label flips with the favourite state. When logged in the menu stays open
 * so the label updates after toggling; when logged out the toggle redirects
 * to login, so the menu dismisses.
 */
export function buildFavoritesMenuItem({
  defaultLabel,
  activeLabel,
  isFavorite,
  isLoggedIn,
}: BuildFavoritesMenuItemArgs): FavoritesMenuItem {
  return {
    button: { alias: 'favorite' },
    actions: [{ type: 'toggleFavorite' }],
    dismiss_on_action: !isLoggedIn,
    label: (isFavorite ? activeLabel : defaultLabel) ?? '',
    icon: 'favorite',
  };
}

/**
 * Returns the entry with `extensions.entry_action` replaced by the resolved
 * items, where favourites items are swapped for the prebuilt favourites item.
 * Repeated items keep their ids; the popup-menu data hook makes them unique.
 */
export function buildResolvedContextualEntry({
  entry,
  items,
  favoritesItem,
}: BuildResolvedContextualEntryArgs): FeedEntry {
  const resolvedEntryActions: Array<PopupMenuEntryAction | FavoritesMenuItem> =
    items.map((item: ResolvedPopupMenuItem) =>
      item.kind === 'favorites' ? favoritesItem : item.entryAction
    );

  return {
    ...entry,
    extensions: {
      ...(isRecord(entry?.extensions) ? entry.extensions : {}),
      entry_action: resolvedEntryActions,
    },
  } as FeedEntry;
}

/**
 * Resolution state for the popup_menu round-icon flow: the entry with its
 * menu items resolved from the configured actions list, and whether the menu
 * has anything to render.
 */
export function getResolvedPopupMenuState({
  popupMenuActionsConfig,
  entry,
  favoritesAvailable,
  favoritesItem,
}: GetResolvedPopupMenuStateArgs): PopupMenuResolvedState {
  const { items, hasRenderableItems }: ResolvedPopupMenuItems =
    resolvePopupMenuActionItems({
      identifiers: popupMenuActionsConfig,
      entry,
      favoritesAvailable,
    });

  return {
    contextualEntry: buildResolvedContextualEntry({
      entry,
      items,
      favoritesItem,
    }),
    hasRenderableActions: hasRenderableItems,
  };
}
